import React from 'react';
import {Pressable, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import type {Poll} from '@/Hooks/ClubsApi/types';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

// A poll rendered inline in the club chat (design lines 361–373).
export default function PollCard({
  poll,
  onVote,
  onClose,
  canClose,
  mine = false,
}: {
  poll: Poll;
  onVote: (optionId: number) => void;
  onClose?: () => void;
  canClose?: boolean;
  mine?: boolean;
}) {
  const voted = poll.my_vote_option_id != null;
  const isClosed = poll.closed;
  const showResults = voted || isClosed;
  const {isMobile} = useMediaQuery();
  const isWide = !isMobile;

  return (
    <View
      testID={`poll-card-${poll.id}`}
      style={{
        // Align like a chat message: right if I created it, left otherwise.
        alignSelf: mine ? 'flex-end' : 'flex-start',
        width: isWide ? '50%' : '90%',
        backgroundColor: '#fff',
        borderRadius: 14,
        padding: 14,
        marginBottom: 11,
        borderWidth: 1,
        borderColor: 'rgba(38,35,34,0.10)',
        shadowColor: '#4D3F37',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.05,
        shadowRadius: 2,
      }}>
      {/* Eyebrow */}
      <CCText
        style={{
          fontSize: 11,
          fontWeight: '700',
          color: '#3DAB9E',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}>
        📊 Poll
      </CCText>
      <CCText
        style={{
          fontSize: 14.5,
          fontWeight: '600',
          color: '#262322',
          marginTop: 6,
          marginBottom: 10,
        }}>
        {poll.question}
      </CCText>

      {/* Options */}
      <View style={{gap: 8}}>
        {poll.options.map(o => {
          const mine = poll.my_vote_option_id === o.id;
          return (
            <Pressable
              key={o.id}
              testID={`poll-option-${o.id}`}
              disabled={voted || isClosed}
              onPress={() => onVote(o.id)}
              style={{
                position: 'relative',
                borderWidth: 1,
                borderColor: 'rgba(38,35,34,0.10)',
                borderRadius: 9,
                overflow: 'hidden',
              }}>
              {/* Result fill bar — width = pct; my choice aqua, others pale aqua. */}
              {showResults ? (
                <View
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${o.pct}%`,
                    backgroundColor: mine ? '#3DAB9E' : '#C9E4E0',
                    opacity: 0.4,
                  }}
                />
              ) : null}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingVertical: 9,
                  paddingHorizontal: 12,
                }}>
                <CCText
                  style={{fontSize: 13.5, fontWeight: '600', color: '#262322'}}>
                  {o.text}
                </CCText>
                {showResults ? (
                  <CCText
                    style={{
                      fontSize: 13.5,
                      fontWeight: '700',
                      color: '#4D3F37',
                    }}>
                    {o.pct}%
                  </CCText>
                ) : null}
              </View>
            </Pressable>
          );
        })}
      </View>

      {/* Footer */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 9,
        }}>
        <CCText style={{fontSize: 11.5, color: '#888280'}}>
          {poll.total_votes} vote{poll.total_votes !== 1 ? 's' : ''}
          {isClosed ? ' · Closed' : ''}
        </CCText>
        {canClose && !isClosed ? (
          <Pressable testID={`poll-close-${poll.id}`} onPress={onClose}>
            <CCText
              style={{fontSize: 11.5, fontWeight: '600', color: '#3DAB9E'}}>
              Close poll
            </CCText>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}
